import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Award, Hand, TrendingUp, ClipboardList, FileText, Users, CheckCircle, Star, ArrowRight, MapPin, Download, Laptop, Home as HomeIcon, Heart, Move, User, Activity } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '../components/ui/carousel';
import HeroCarousel from '../components/HeroCarousel';
import { clinicInfo, whyMindMotion, conditions, howItWorks, testimonials, helpfulResources } from '../data/mockData';

const HomePage = () => {
  const [currentImage, setCurrentImage] = useState(0);
  const [testimonialsApi, setTestimonialsApi] = useState(null);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const whyImages = ['/images/wmm2.jpg', '/images/wmm4.jpg', '/images/wmm5.jpg'];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % whyImages.length);
    }, 4000); // Auto-advance every 4 seconds

    return () => clearInterval(timer);
  }, [whyImages.length]);

  useEffect(() => {
    if (!testimonialsApi) {
      return;
    }

    const onSelect = () => {
      setActiveTestimonial(testimonialsApi.selectedScrollSnap());
    };

    onSelect();
    testimonialsApi.on('select', onSelect);
    testimonialsApi.on('reInit', onSelect);

    const autoPlayTimer = setInterval(() => {
      testimonialsApi.scrollNext();
    }, 4500);

    return () => {
      clearInterval(autoPlayTimer);
      testimonialsApi.off('select', onSelect);
      testimonialsApi.off('reInit', onSelect);
    };
  }, [testimonialsApi]);

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % whyImages.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + whyImages.length) % whyImages.length);
  };

  const iconMap = {
    Award,
    Hand,
    TrendingUp,
    CheckCircle
  };

  const resourceIconMap = {
    Laptop,
    Home: HomeIcon,
    Heart,
    Move,
    User,
    Activity
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${clinicInfo.whatsapp.replace(/[^0-9]/g, '')}?text=Hi, I would like to book an appointment`, '_blank');
  };

  const handleCall = () => {
    window.location.href = `tel:${clinicInfo.phone}`;
  };

  const handleDownload = (resource) => {
    window.open(resource.downloadLink, '_blank');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Carousel Section */}
      <HeroCarousel />

      {/* Why Mind Motion Section - 2 Column Layout */}
      <section className="py-12 md:py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
              Why Mind Motion?
            </h2>
            <p className="text-lg text-muted-foreground">
              Evidence-based physiotherapy that delivers real results
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-10 items-center">
            {/* Left Column - Features */}
            <div className="space-y-6">
              {whyMindMotion.map((item) => {
                const Icon = iconMap[item.icon];
                return (
                  <div key={item.id} className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-14 h-14 bg-secondary rounded-full flex items-center justify-center">
                      <Icon className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-primary mb-2">{item.title}</h3>
                      <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Right Column - Clinic Images Slideshow */}
            <div className="relative">
              <img 
                src={whyImages[currentImage]} 
                alt="Mind Motion Clinic"
                className="rounded-lg shadow-lg w-full h-full object-contain"
              />
              <button 
                onClick={prevImage}
                className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75"
              >
                ‹
              </button>
              <button 
                onClick={nextImage}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75"
              >
                ›
              </button>
              <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-2">
                {whyImages.map((_, index) => (
                  <div 
                    key={index}
                    className={`w-2 h-2 rounded-full ${index === currentImage ? 'bg-white' : 'bg-white bg-opacity-50'}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conditions We Help With */}
      <section className="py-12 md:py-16 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
              Conditions We Help With
            </h2>
            <p className="text-lg text-muted-foreground">
              Specialized treatment for a wide range of musculoskeletal conditions
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {conditions.slice(0, 6).map((condition) => (
              <Link 
                key={condition.id} 
                to={`/treatment/${condition.id}`}
                className="group"
              >
                <Card className="overflow-hidden border-2 border-gray-100 hover:border-primary transition-all duration-300 hover:shadow-lg h-full">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={condition.image} 
                      alt={condition.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-primary mb-2 group-hover:text-primary/80 transition-colors">
                      {condition.name}
                    </h3>
                    <p className="text-muted-foreground text-sm mb-4">{condition.shortDesc}</p>
                    <span className="text-primary font-medium text-sm inline-flex items-center">
                      Learn more <ArrowRight className="ml-1 w-4 h-4" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/treatments">
              <Button 
                size="lg"
                className="px-8 transition-all duration-300"
              >
                View All Treatments
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How We Help You */}
      <section className="py-12 md:py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
              How We Help You
            </h2>
            <p className="text-lg text-muted-foreground">
              Go from pain to recovery in 4 simple steps
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {howItWorks.map((step, index) => {
              return (
                <div key={step.step} className="relative text-center">
                  <div className="relative mb-6">
                    <img 
                      src={`/images/hwhy${step.step}.jpg`} 
                      alt={step.description}
                      className="w-72 h-48 object-cover rounded-lg mx-auto" 
                    />
                    <div className="absolute top-4 right-4 flex items-center justify-center">
                      <div className="w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center text-xl font-bold">
                        {step.step}
                      </div>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-primary mb-3">{step.title}</h3>
                  <p className="text-muted-foreground">{step.description}</p>
                  {index < howItWorks.length - 1 && (
                    <div className="hidden md:block absolute top-24 left-[60%] w-[80%] h-0.5 bg-secondary" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 md:py-16 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
              What Our Patients Say
            </h2>
            <p className="text-lg text-muted-foreground">
              Real stories from real people who found relief at Mind Motion
            </p>
            <a 
              href="https://www.google.com/search?q=Mind+Motion+Physiotherapy+and+Rehab+Centre+reviews" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center text-primary hover:text-primary/80 font-medium mt-2"
            >
              View all reviews on Google <ArrowRight className="ml-1 w-4 h-4" />
            </a>
          </div>
          <div className="px-2 sm:px-10">
            <Carousel
              setApi={setTestimonialsApi}
              opts={{
                align: 'start',
                loop: true
              }}
              className="w-full"
            >
              <CarouselContent>
                {testimonials.map((testimonial) => (
                  <CarouselItem key={testimonial.id} className="sm:basis-1/2">
                    <Card className="border-2 border-gray-100 hover:shadow-lg transition-all duration-300 h-full">
                      <CardContent className="p-6 h-full flex flex-col">
                        <div className="flex gap-1 mb-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <Star key={i} className="w-5 h-5 fill-[#FFB800] text-[#FFB800]" />
                          ))}
                        </div>
                        <p className="text-muted-foreground mb-4 leading-relaxed italic flex-1">"{testimonial.text}"</p>
                        <div className="border-t pt-4">
                          <p className="font-bold text-primary">{testimonial.name}</p>
                          <p className="text-sm text-muted-foreground">{testimonial.condition}</p>
                          <p className="text-xs text-muted-foreground/70 mt-1 flex items-center">
                            <MapPin className="w-3 h-3 mr-1" />
                            {testimonial.location}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="hidden sm:flex" />
              <CarouselNext className="hidden sm:flex" />
            </Carousel>
            <div className="flex justify-center gap-2 mt-6">
              {testimonials.map((testimonial, index) => (
                <button
                  key={testimonial.id}
                  type="button"
                  onClick={() => testimonialsApi?.scrollTo(index)}
                  className={`h-2.5 rounded-full transition-all ${index === activeTestimonial ? 'w-6 bg-primary' : 'w-2.5 bg-primary/30 hover:bg-primary/50'}`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Bring Them Home Resources Section */}
      <section className="py-12 md:py-16 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
              Stop Guessing. Start Understanding Your Body.
            </h2>
            <p className="text-lg text-muted-foreground mb-2">
              Real life. Real bodies. Real solutions.
            </p>
            <p className="text-lg text-muted-foreground">
              Not generic advice—guides designed for how you actually live. Understand what your body is telling you, prevent avoidable pain, and make smarter choices to move, feel, and live better every day.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {helpfulResources.slice(0, 6).map((resource) => {
              const Icon = resourceIconMap[resource.icon];
              return (
                <Card key={resource.id} className="border-2 border-gray-100 hover:border-primary transition-all duration-300 hover:shadow-lg group">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-secondary rounded-full group-hover:bg-primary transition-colors duration-300">
                        <Icon className="w-6 h-6 text-primary group-hover:text-white transition-colors duration-300" />
                      </div>
                      <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {resource.category}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
                      {resource.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                      {resource.description}
                    </p>
                    <Button 
                      onClick={() => handleDownload(resource)}
                      className="w-full bg-primary hover:bg-[hsl(var(--facebook-blue-hover))] text-white transition-all duration-300"
                      size="sm"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download PDF
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="text-center">
            <Link to="/resources">
              <Button 
                size="lg"
                variant="outline"
                className="px-8 transition-all duration-300"
              >
                View All Resources
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Start Your Recovery Journey
          </h2>
          <p className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Book your comprehensive assessment today and take the first step towards pain-free movement.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={handleWhatsApp}
              size="lg"
              className="bg-primary hover:bg-[hsl(var(--facebook-blue-hover))] text-white text-lg px-8 py-6 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Book on WhatsApp
            </Button>
            <Button 
              onClick={handleCall}
              size="lg"
              variant="outline"
              className="bg-white text-primary hover:bg-gray-100 border-2 border-white text-lg px-8 py-6 transition-all duration-300"
            >
              Call {clinicInfo.phone}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
